import { SUPPORTED_LANGUAGES } from '../constant';
import { SearchMarket, SearchType, SearchUser, ApiResponse, OmitFirstArg, BaseFetchParams } from '../types';
import { BaseTokenConfig } from '../types/app';
import ky, { KyInstance, Options } from 'ky';

// Type definitions
type UploadFileApiResult = {
  fileKey: string;
};

/**
 * Downloads a file as blob for a given file key.
 * @param api - The KyInstance
 * @param fileKey - The file key
 * @param options - Additional request options
 * @returns Promise resolving to file blob
 * @example
 * ```typescript
 * const fileBlob = await getFile(api, 'fileKey');
 * ```
 */
export const getFile = async (api: KyInstance, fileKey: string, options?: Options): Promise<Blob> => {
  return await api
    .get('download-file', {
      headers: { 'content-type': 'application/octet-stream' },
      ...options,
      searchParams: { fileKey: fileKey.trim() },
    })
    .blob();
};

/**
 * Generates a file URL for a given file key.
 * @param fileKey - The file key
 * @param apiUrl - The API base URL (defaults to '/base')
 * @returns The complete file URL
 * @example
 * ```typescript
 * const fileUrl = getFileUrl('fileKey');
 * ```
 */
export const getFileUrl = (fileKey: string, apiUrl: string = '/base'): string => {
  return `${apiUrl}/download-file?fileKey=${encodeURIComponent(fileKey.trim())}`;
};

/**
 * Uploads a file to the server and returns the file key.
 * @param api - The KyInstance
 * @param file - The file to upload
 * @param options - Additional request options
 * @returns Promise resolving to API response with file key
 * @example
 * ```typescript
 * const response = await uploadFile(api, file);
 * console.log(response.data.fileKey);
 * ```
 */
export const uploadFile = async (
  api: KyInstance,
  file: File,
  options?: Options
): Promise<ApiResponse<UploadFileApiResult>> => {
  if (!file) {
    throw new Error('File is required for upload');
  }
  if (file.size === 0) {
    throw new Error('Cannot upload empty file');
  }
  const body = new FormData();
  body.append('file', file);
  body.append('fileName', file.name);
  body.append('contentType', file.type || 'application/octet-stream');
  return await api
    .post<ApiResponse<UploadFileApiResult>>('upload-file', {
      ...options,
      body,
    })
    .json();
};

export type SearchParams = BaseFetchParams & {
  keyword: string;
  searchType: SearchType;
  marketPage: number;
  marketPageSize: number;
  userPage: number;
  userPageSize: number;
};

export type SearchApiResult = {
  userTotal: number;
  users: SearchUser[];
  marketTotal: number;
  markets: SearchMarket[];
};

/**
 * Searches for users and markets based on provided criteria.
 * @param api - The KyInstance
 * @param searchParams - The search parameters
 * @param options - Additional request options
 * @returns Promise resolving to search results
 * @example
 * ```typescript
 * const results = await search(api, {
 *   keyword: 'example',
 *   searchType: SearchType.ALL,
 *   marketPage: 1,
 *   marketPageSize: 10,
 *   userPage: 1,
 *   userPageSize: 10,
 * });
 * ```
 */
export const search = async (
  api: KyInstance,
  searchParams: SearchParams,
  options?: Options
): Promise<ApiResponse<SearchApiResult>> => {
  return await api
    .get<ApiResponse<SearchApiResult>>('search', {
      ...options,
      searchParams,
    })
    .json();
};

export type BaseTokenConfigApiResult = {
  baseTokens: BaseTokenConfig[];
};

/**
 * Retrieves the base token configuration.
 * @param api - The KyInstance
 * @param searchParams - Optional search parameters
 * @param options - Additional request options
 * @returns Promise resolving to base token config
 * @example
 * ```typescript
 * const config = await getBaseTokensConfig(api);
 * ```
 */
export const getBaseTokensConfig = async (
  api: KyInstance,
  options?: Options
): Promise<ApiResponse<BaseTokenConfigApiResult>> => {
  return await api.get<ApiResponse<BaseTokenConfigApiResult>>('get-base-token-config', options).json();
};

export type TranslateBody = {
  text: string;
  sourceLang: keyof typeof SUPPORTED_LANGUAGES;
  targetLang: keyof typeof SUPPORTED_LANGUAGES;
};

export type TranslateApiResult = {
  translatedText: string;
  sourceLang: keyof typeof SUPPORTED_LANGUAGES;
  targetLang: keyof typeof SUPPORTED_LANGUAGES;
  cached: boolean;
  tokensUsed: number;
};

export const translate = async (api: KyInstance, json: TranslateBody, options?: Options) => {
  return await api.post<ApiResponse<TranslateApiResult>>('translate', { ...options, json }).json();
};

// API interface definition
export type BaseApi = {
  /**
   * Generates a file URL for a given file key.
   */
  getFileUrl: typeof getFileUrl;

  /**
   * Downloads a file as blob for a given file key.
   */
  getFile: OmitFirstArg<typeof getFile>;

  /**
   * Uploads a file to the server and returns the file key.
   */
  uploadFile: OmitFirstArg<typeof uploadFile>;

  /**
   * Searches for users and markets based on provided criteria.
   */
  search: OmitFirstArg<typeof search>;

  /**
   * Retrieves the base token configuration.
   */
  getBaseTokensConfig: OmitFirstArg<typeof getBaseTokensConfig>;

  /**
   * Translates text from one language to another.
   */
  translate: OmitFirstArg<typeof translate>;
};

/**
 * Creates a base API instance with the provided configuration.
 * @param initOptions - Initial options for the API instance
 * @returns Configured BaseApi instance
 * @example
 * ```typescript
 * const baseApi = createBaseApi({
 *   prefixUrl: 'https://api.example.com',
 *   timeout: 10000
 * });
 * ```
 */
export const createBaseApi = (initOptions: Options): BaseApi => {
  const mergedOptions: Options = { ...initOptions, prefixUrl: `${initOptions.prefixUrl}/base` };
  const baseApi = ky.create(mergedOptions);
  return {
    getFileUrl: (fileKey: string, apiUrl = mergedOptions.prefixUrl as string) => getFileUrl(fileKey, apiUrl),
    getFile: getFile.bind(null, baseApi),
    uploadFile: uploadFile.bind(null, baseApi),
    search: search.bind(null, baseApi),
    getBaseTokensConfig: getBaseTokensConfig.bind(null, baseApi),
    translate: translate.bind(null, baseApi),
  };
};

export default createBaseApi;
